import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Box,
  LinearProgress,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getAllClassrooms, getStudentsByClassroom } from "../../api/sponteAPI";
import {
  normalizeClassrooms,
  normalizeStudentsByClassroom,
} from "../../utils/sponteDataNormalizer";
import { transferStudentBetweenClassrooms } from "../../api/endpoints/classroomTransfer";

/**
 * Modal para mudança de turma do aluno no Sponte + banco local.
 * Alunos e turmas de destino são carregados só ao abrir (evita custo ao entrar na tela de notas).
 */
export function TransferStudentDialog({
  open,
  onClose,
  anoLetivo,
  turmaOrigemId,
  turmaOrigemNome,
  unit,
  onTransferred,
}) {
  const [destTurmas, setDestTurmas] = useState([]);
  const [dialogStudents, setDialogStudents] = useState([]);
  /** Um único estado: formulário só aparece quando false */
  const [listsLoading, setListsLoading] = useState(false);
  const [alunoId, setAlunoId] = useState("");
  const [destTurmaId, setDestTurmaId] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  /** Falha ao buscar listas no Sponte (oculta o formulário) */
  const [loadError, setLoadError] = useState(null);
  /** Validação ou erro da API ao confirmar transferência */
  const [formError, setFormError] = useState(null);
  /** Resposta da API após sucesso — mantém o modal aberto até o usuário fechar */
  const [successResult, setSuccessResult] = useState(null);

  const sortedStudents = useMemo(() => {
    if (!Array.isArray(dialogStudents)) return [];
    return [...dialogStudents].sort((a, b) => {
      const na = parseInt(a.NumeroChamada, 10) || 0;
      const nb = parseInt(b.NumeroChamada, 10) || 0;
      return na - nb;
    });
  }, [dialogStudents]);

  const selectedDestNome = useMemo(() => {
    if (!destTurmaId) return "";
    const t = destTurmas.find((x) => String(x.TurmaID) === String(destTurmaId));
    return t?.Nome || `Turma ${destTurmaId}`;
  }, [destTurmas, destTurmaId]);

  const selectedAlunoNome = useMemo(() => {
    if (!alunoId) return "";
    const s = sortedStudents.find((x) => String(x.AlunoID) === String(alunoId));
    if (!s) return "";
    const ch = s.NumeroChamada ? `${s.NumeroChamada} — ` : "";
    return `${ch}${s.Nome || s.AlunoID}`;
  }, [sortedStudents, alunoId]);

  useEffect(() => {
    if (!open || !anoLetivo || turmaOrigemId == null || turmaOrigemId === "") {
      return undefined;
    }

    let cancelled = false;
    setListsLoading(true);
    setLoadError(null);
    setFormError(null);
    setDestTurmaId("");
    setAlunoId("");
    setSuccessResult(null);

    const turmasPromise = getAllClassrooms(anoLetivo).then((raw) => {
      if (cancelled) return [];
      const list = normalizeClassrooms(raw).filter(
        (t) => Number(t.TurmaID) !== Number(turmaOrigemId)
      );
      list.sort((a, b) =>
        String(a.Nome || "").localeCompare(String(b.Nome || ""), "pt-BR")
      );
      return list;
    });

    const alunosPromise = getStudentsByClassroom(turmaOrigemId).then((res) => {
      if (cancelled) return [];
      return normalizeStudentsByClassroom(res);
    });

    Promise.all([turmasPromise, alunosPromise])
      .then(([turmasList, studentsList]) => {
        if (cancelled) return;
        setDestTurmas(Array.isArray(turmasList) ? turmasList : []);
        setDialogStudents(Array.isArray(studentsList) ? studentsList : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err?.message || "Não foi possível carregar turmas ou alunos.");
          setDestTurmas([]);
          setDialogStudents([]);
        }
      })
      .finally(() => {
        if (!cancelled) setListsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, anoLetivo, turmaOrigemId]);

  const handleClose = () => {
    if (submitting) return;
    setSuccessResult(null);
    setFormError(null);
    setLoadError(null);
    setObservacoes("");
    setAlunoId("");
    setDestTurmaId("");
    onClose();
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!alunoId || !destTurmaId || !turmaOrigemId) {
      setFormError("Selecione o aluno e a turma de destino.");
      return;
    }

    try {
      setSubmitting(true);
      const data = await transferStudentBetweenClassrooms({
        unit,
        AlunoID: alunoId,
        TurmaOrigemID: turmaOrigemId,
        TurmaDestinoID: destTurmaId,
        Observacoes: observacoes.trim() || undefined,
      });
      if (typeof onTransferred === "function") {
        await onTransferred();
      }
      setSuccessResult(data);
    } catch (err) {
      const apiMsg = err.response?.data?.error || err.message;
      setFormError(apiMsg || "Falha na transferência.");
    } finally {
      setSubmitting(false);
    }
  };

  const local = successResult?.local;

  const showForm = !successResult && !listsLoading && !loadError;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {successResult ? (
          <CheckCircleOutlineIcon color="success" />
        ) : (
          <SwapHorizIcon color="primary" />
        )}
        {successResult ? "Transferência concluída" : "Mudança de turma no Sponte"}
      </DialogTitle>
      {!successResult && listsLoading && (
        <LinearProgress color="primary" aria-label="Carregando dados no Sponte" />
      )}
      <DialogContent aria-busy={!successResult && listsLoading}>
        {successResult ? (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {successResult.message ||
                  "Aluno transferido no Sponte e dados locais atualizados."}
              </Typography>
              {selectedAlunoNome && (
                <Typography variant="body2">
                  <strong>Aluno:</strong> {selectedAlunoNome}
                </Typography>
              )}
              {selectedDestNome && (
                <Typography variant="body2">
                  <strong>Nova turma:</strong> {selectedDestNome}
                </Typography>
              )}
            </Alert>
            {local && (
              <Typography variant="body2" color="text.secondary" component="div">
                <strong>O que foi atualizado no Autêntico</strong>
                <ul style={{ margin: "8px 0 0 0", paddingLeft: "1.25rem" }}>
                  <li>
                    Notas (grades):{" "}
                    <strong>{local.gradesRowsUpdated ?? 0}</strong> linha(s)
                  </li>
                  <li>
                    Situação das notas (notas_situacao):{" "}
                    <strong>{local.notasSituacaoRowsUpdated ?? 0}</strong> linha(s)
                  </li>
                  <li>
                    Vínculo aluno–turma (class_users):{" "}
                    <strong>
                      {local.classUsersSynced
                        ? "sincronizado"
                        : "não aplicado (sem usuário estudante local com este ID no Sponte)"}
                    </strong>
                  </li>
                </ul>
              </Typography>
            )}
            {successResult.sponte?.retornoOperacao != null && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                Confirmação no Sponte (código de retorno):{" "}
                {String(successResult.sponte.retornoOperacao)}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Origem: <strong>{turmaOrigemNome || turmaOrigemId}</strong>. No Sponte a situação na nova turma é enviada como{" "}
              <strong>cursando</strong> (ID -3). Notas já lançadas neste sistema são atualizadas para a turma de destino.
            </Typography>

            {listsLoading && (
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  textAlign: "center",
                }}
              >
                <CircularProgress size={48} thickness={4} aria-label="Carregando" />
                <Typography variant="body1" fontWeight={600}>
                  Carregando alunos e turmas…
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
                  Buscando no Sponte a lista desta turma e as turmas de destino do ano letivo.
                </Typography>
              </Box>
            )}

            {!listsLoading && loadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {loadError}
              </Alert>
            )}

            {showForm && (
              <>
                {formError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {formError}
                  </Alert>
                )}
                <FormControl fullWidth sx={{ mb: 2 }} disabled={submitting} variant="outlined">
                  {/* shrink: evita sobreposição label × texto quando value="" + displayEmpty (bug comum do MUI Select) */}
                  <InputLabel id="transfer-aluno-label" shrink>
                    Aluno
                  </InputLabel>
                  <Select
                    labelId="transfer-aluno-label"
                    label="Aluno"
                    value={alunoId}
                    onChange={(e) => setAlunoId(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      <em>Selecione o aluno</em>
                    </MenuItem>
                    {sortedStudents.length === 0 ? (
                      <MenuItem disabled value="no-students">
                        Nenhum aluno nesta turma no Sponte
                      </MenuItem>
                    ) : (
                      sortedStudents.map((s) => (
                        <MenuItem key={String(s.AlunoID)} value={String(s.AlunoID)}>
                          {s.NumeroChamada ? `${s.NumeroChamada} — ` : ""}
                          {s.Nome || s.AlunoID}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }} disabled={submitting} variant="outlined">
                  <InputLabel id="transfer-dest-label" shrink>
                    Turma de destino
                  </InputLabel>
                  <Select
                    labelId="transfer-dest-label"
                    label="Turma de destino"
                    value={destTurmaId}
                    onChange={(e) => setDestTurmaId(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      <em>Selecione a turma de destino</em>
                    </MenuItem>
                    {destTurmas.length === 0 ? (
                      <MenuItem disabled value="no-classrooms">
                        Nenhuma turma de destino disponível
                      </MenuItem>
                    ) : (
                      destTurmas.map((t) => (
                        <MenuItem key={String(t.TurmaID)} value={String(t.TurmaID)}>
                          {t.Nome || `Turma ${t.TurmaID}`}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                <TextField
                  label="Observações (opcional)"
                  fullWidth
                  multiline
                  minRows={2}
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  disabled={submitting}
                  inputProps={{ maxLength: 500 }}
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {successResult ? (
          <Button onClick={handleClose} variant="contained" color="primary">
            Fechar
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting} color="inherit">
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={
                submitting ||
                listsLoading ||
                !alunoId ||
                !destTurmaId ||
                sortedStudents.length === 0 ||
                destTurmas.length === 0
              }
            >
              {submitting ? <CircularProgress size={22} /> : "Confirmar transferência"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
